// react
import React, { useEffect, useState, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'

// css
import './styles.scss'

// components
import {
  Button,
  BUTTON_THEME,
  Icon,
  ICON_TYPES,
  IconButton,
  BarLoader,
} from '@st-operations/uikit/src/v2/index.js'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'
import FAQ from '_components/FAQ.jsx'
import Header from '_components/header/index.jsx'
import SelectSlidesModal from '_components/audience-insights/SelectSlidesModal.jsx'
import ExportDialogContent from '_components/audience-insights/ExportDialogContent.jsx'
import AudienceInsightsLayout from '_components/audience-insights/AudienceInsightsLayout'
import { SLIDES_SCHEMA } from '_components/audience-insights/constants/slidesSchema'
import { EDIT_FIELDS } from '_components/audience-insights/constants/availableSlides'

// hooks
import useModal from '../../hooks/useModal'
import { useRetractSidebar } from '_hooks'

// libraries
import sendAnalytics from '@st-operations/uikit/lib/sendAnalytics.js'
import pretty from '@st-operations/uikit/lib/pretty'
import User from '_lib/User.js'
import parseAudiences from '_lib/parseReports.js'
import { logErrorAsync } from '_lib/utility'

import { getAudience } from '_services/insights'
import { getReport, updateReport, getCategoryOverindex } from '_services/insights-v2'

const INITIAL_STATE = {
  duplicated_idx: {},
  activeSlideIndex: 0,
  show_faq: false,
  report: null,
}

const populateDefaultData = (categoryOverindex) => (report, index) => {
  const currentSlide = report?.slides?.[index]

  const populateCategories = (categoriesLevels, quantity) => {
    // find top categories from insights
    const scores = categoriesLevels.flatMap((level) => Object.entries(level ?? {}))
    scores.sort((a, b) => b[1] - a[1])

    // format
    const entities = scores.slice(0, quantity).map(([key, value]) => ({
      originalKey: key,
      value,
      key: pretty(key.split('/').at(-1)),
    }))

    return entities
  }

  switch (currentSlide?.template) {
    // Passion Points
    case 'L2_Categories_and_L3_Categories':
      if (!currentSlide?.data?.selected_entities?.length) {
        const entities = populateCategories([categoryOverindex?.L2, categoryOverindex?.L3], 8)
        // mutates `report`
        currentSlide.data.selected_entities = entities
      }
      break
    // Category Cloud
    case 'L2_and_L3_Categories_WordCloud':
      if (!currentSlide?.data?.selected_entities?.length) {
        const entities = populateCategories([categoryOverindex?.L2, categoryOverindex?.L3], 8)
        // mutates `report`
        currentSlide.data.selected_entities = entities
      }
      break
    default:
      break
  }
  return currentSlide
}

const addUID = (report) => ({
  ...report,
  slides: report.slides.map((slide) => ({ ...slide, uid: slide.uid ?? uuidv4() })),
})

const removeUID = (slides) =>
  slides.map((slide) => {
    delete slide.uid
    return slide
  })

const convertOverindexStringsToNumbers = (data) =>
  Object.fromEntries(
    Object.entries(data).map(([level, content]) => [
      level,
      Object.fromEntries(
        Object.entries(content).map(([category, value]) => [category, Number(value)])
      ),
    ])
  )

const useLoadData = ({ audienceId }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState({ message: '', error: null })

  const [audience, setAudience] = useState()
  const [report, setReport] = useState()
  const [categoryOverindex, setCategoryOverindex] = useState(null)

  useEffect(() => {
    if (audience && report) {
      setLoading(false)
    }
  }, [audience, report])

  useEffect(() => {
    if (error.error) {
      setLoading(false)
      toast.error(error.message)
      logErrorAsync(error.error)
    }
  }, [JSON.stringify(error)])

  const loadData = () => {
    // setup
    setLoading(true)
    setError({ message: '', error: null })

    loadAudience()
    loadReport()
    loadCategories()
  }

  const loadAudience = () => {
    getAudience({ id: audienceId })
      .then((audience) => {
        // NOTE: what does that means?
        if (audience.name) {
          audience = parseAudiences([audience])[0]

          // event tracking
          sendAnalytics('read_insights', audience)
        }
        setAudience(audience)
      })
      .catch((error) =>
        setError({
          message: 'Error loading audience',
          error,
        })
      )
  }

  const loadReport = () => {
    getReport({ audienceId })
      .then((report) => {
        const reportWithUID = report?.slides ? addUID(report) : report
        setReport(reportWithUID)
      })
      .catch((error) =>
        setError({
          message: 'Error loading report!',
          error,
        })
      )
  }

  const loadCategories = () => {
    getCategoryOverindex({ audienceId })
      .then((data) => {
        const formatted = convertOverindexStringsToNumbers(data)
        setCategoryOverindex(formatted)
      })
      .catch((error) => {
        setError({
          message: error.message,
          error,
        })
      })
  }

  return {
    loadData,
    audience,
    report,
    categoryOverindex,
    loading,
    error,
  }
}

// page
const AudienceInsights = (props) => {
  useRetractSidebar()
  const audienceId = props.match.params.id

  const [state, setState] = useState(INITIAL_STATE)
  const report = state.report // report is necessary to be a state in this component so it can be edited

  const selectSlidesModal = useModal(false)
  const exportModal = useModal(false)

  const updateState = (payload, callback) => {
    setState((prevState) => ({ ...prevState, ...payload }))
    if (callback) {
      callback()
    }
  }

  const { loadData, audience, report: respReport, loading, categoryOverindex, error } = useLoadData(
    {
      audienceId,
    }
  )

  useEffect(() => {
    updateState({ report: respReport })
  }, [respReport])

  useEffect(() => {
    // populate outro slide if it's the first report
    if (audience && respReport?.slides?.[0]?.data?.section_1?.title === 'Intro Slide Title') {
      populateIntro(audience, respReport)
      populateOutro(respReport)
    }
  }, [respReport, audience])

  const scrollWithKeyboard = useCallback(
    (e) => {
      const { activeSlideIndex } = state
      if (e.key === 'ArrowUp') {
        updateState({ activeSlideIndex: Math.max(0, activeSlideIndex - 1) })
      }
      if (e.key === 'ArrowDown') {
        updateState({ activeSlideIndex: Math.min(report.slides.length - 1, activeSlideIndex + 1) })
      }
    },
    [report, state.activeSlideIndex]
  )

  useEffect(() => {
    window.addEventListener('keydown', scrollWithKeyboard)

    return () => {
      window.removeEventListener('keydown', scrollWithKeyboard)
    }
  }, [scrollWithKeyboard])

  useEffect(() => {
    // Load audience when user is updated
    if (state.user) {
      loadData()
    }
  }, [state.user])

  useEffect(() => {
    User.watch({ setState: updateState })
  }, [])

  // Show slide selector if it's a new report, only once
  useEffect(() => {
    if (report?.message === 'No report found' && !selectSlidesModal.lastEvent) {
      selectSlidesModal.handleOpenModal()
    }
  }, [report, selectSlidesModal.lastEvent])

  const onChangeSlideOrder = (newList) =>
    // NOTE: changes report
    setState((prevState) => ({ ...prevState, report: { ...prevState.report, slides: newList } }))

  // TODO: refactor
  const onFieldChange = ({ index, value, field, type }) => {
    let slide = report.slides[index]

    let first_level = [
      'selected_entities',
      'selected_events',
      'time_frame',
      'time_range',
      'y_labels',
      'entities_order',
    ]
    if (field === EDIT_FIELDS.TOPICS) {
      slide.data['selected_entities'] = [...new Set(value)]
    } else if (field === EDIT_FIELDS.TRANSITION_TITLE) {
      slide.data[field] = value
    } else if (field === EDIT_FIELDS.RECOMMENDED_SEGMENTS_3) {
      slide.data.section_3[type] = value
    } else if (field === EDIT_FIELDS.LOOKBACK) {
      slide.data.lookback = value
    } else if (field === EDIT_FIELDS.SOCIAL_CHANNELS) {
      slide.data.selected_channels = [...value]
    } else if (field === EDIT_FIELDS.HEADER || first_level.includes(type)) {
      // update first level data: title, description, selected_entities time_frame
      if (slide.data[type] || slide.data[type] === '') {
        slide.data[type] = value

        // update selected_entities in section
      } else if (slide.data[field] || slide.data[field] === '') {
        slide.data[field][type] = value
      }
    } else {
      // update section data if exists
      // eslint-disable-next-line no-lonely-if
      if (slide.data[field] || slide.data[field] === '') {
        slide.data[field][type] = value
      }
    }
    // NOTE: changes report
    updateState({ report })
  }

  const handleSave = () => {
    if (report && audience) {
      const slides = removeUID(report.slides)

      updateReport({ audienceId, slides, audienceName: audience.name })
        .then((report) => {
          updateState({ report: addUID(report) })
          toast('Saved!')
        })
        .catch((error) => {
          toast.error('Error saving report!')
          logErrorAsync(error)
        })

      // event tracking
      sendAnalytics('update_insights', audience)
    } else {
      toast.error('No report found')
    }
  }

  const populateIntro = (audience, report) => {
    let { user } = state
    // eslint-disable-next-line no-unused-vars
    let report_label, report_parent
    if (!audience.label) {
      let labels = report.report_name.split(/\||\s-\s|\s--->\s|\s>\s/)
      report_label = labels[labels.length - 1]
      report_parent = labels.slice(0, -1).join(' > ')
    }
    let title = audience.label || report_label
    if (title) {
      report.slides[0].data.section_1.title = title
    }
    report.title = {
      parent: audience.parent || report.report_name,
      label: audience.label,
    }
    report.slides[0].data.section_1.name = user.name

    // NOTE: changes report
    updateState({ report })
  }

  const populateOutro = (report) => {
    report.slides.forEach((slide) => {
      if (slide.template === 'Outro') {
        slide.data.section_1.name =
          report.created_by.name ?? report.created_by.email.split('@')?.[0] ?? ''
        slide.data.section_1.email = report.created_by.email
      }
    })
    // NOTE: changes report
    updateState({ report })
  }

  const addSlide = (slide) => {
    let { activeSlideIndex } = state

    const copy = (obj) => JSON.parse(JSON.stringify(obj))

    if (slide.template === 'Marketing') {
      const marketingSlides = copy(
        SLIDES_SCHEMA.filter(({ template }) => template.includes('Marketing_'))
      )
      report.slides.splice(activeSlideIndex + 1, 0, ...marketingSlides)
    } else {
      const selectedSlide = copy(SLIDES_SCHEMA.find(({ template }) => template === slide.template))
      report.slides.splice(activeSlideIndex + 1, 0, selectedSlide)
    }

    // NOTE: changes report
    populateDefaultData(categoryOverindex)(report, activeSlideIndex + 1)

    updateState({
      activeSlideIndex: activeSlideIndex + 1,
      // NOTE: changes report
      report: addUID(report),
    })
  }

  const duplicateSlide = () => {
    let { activeSlideIndex, duplicated_idx } = state
    let newSlide = JSON.parse(JSON.stringify(report.slides[activeSlideIndex]))

    // manually add uid
    newSlide.uid = uuidv4()

    // use map to tracking num of duplicated slides for each template
    if (newSlide.template in duplicated_idx) {
      duplicated_idx[newSlide.template] += 1
    } else {
      duplicated_idx[newSlide.template] = 1
    }

    // append copy x to the end of title
    let idx = newSlide.data.title.indexOf('copy')
    let copy = duplicated_idx[newSlide.template]
    if (idx === -1) {
      newSlide.data.title = newSlide.data.title + ' copy ' + copy
    } else {
      newSlide.data.title = newSlide.data.title.slice(0, idx) + ' copy ' + copy
    }

    // duplicate the slide after current slide
    report.slides.splice(activeSlideIndex + 1, 0, newSlide)
    // NOTE: changes report
    updateState({ activeSlideIndex: activeSlideIndex + 1, report })
  }

  const deleteSlide = (idx) => {
    let { activeSlideIndex } = state

    // decrement index if deleting last slide, so index doesn't go out of bounds
    if (idx === report.slides.length - 1) {
      updateState({ activeSlideIndex: activeSlideIndex - 1 })
    }
    report.slides.splice(idx, 1)

    // NOTE: changes report
    updateState({ report })
  }

  return (
    <div id="audience-insights-page" className="audience-insights">
      <Dialog open={loading} onClose={() => false}>
        <h2>Generating Insights...</h2>
        <BarLoader />
      </Dialog>
      <Dialog open={exportModal.isOpen} onClose={exportModal.handleCloseModal}>
        <ExportDialogContent fileName={audience?.name} onClose={exportModal.handleCloseModal} />
      </Dialog>
      <If condition={audience && report?.message === 'No report found'}>
        <SelectSlidesModal
          open={selectSlidesModal.isOpen}
          onClose={selectSlidesModal.handleCloseModal}
          audienceId={audienceId}
          audienceName={audience.name}
          onSuccess={(report) => {
            updateState({ report: addUID(report) })
            selectSlidesModal.handleCloseModal()
          }}
          populateDefault={populateDefaultData(categoryOverindex)}
        />
      </If>
      <Header
        actions={[
          {
            children: <Icon icon="info" color={'green'} />,
            theme: BUTTON_THEME.GHOST,
            onClick: () =>
              setState((state) => {
                return { ...state, show_faq: !state.show_faq }
              }),
          },
        ]}
        className="audience-insights-page__header-2"
      >
        <div className="audience-insights__header-container">
          <IconButton
            iconProps={{
              icon: 'goback',
              type: ICON_TYPES.OUTLINE,
              width: '32px',
              height: '32px',
            }}
            className="audience-insights__icon-back"
            onClick={() => window.history.back()}
            ariaLabel="Go to previous page"
            key="sort-button"
          />
          <div className="audience-insights__header">
            <p className="audience-insights__breadcrumb">
              {audience?.parent || (report?.title?.label && report.title.parent) || ''}
            </p>
            <div>
              <p className="audience-insights__title">
                {audience?.label ?? report?.title?.label ?? report?.report_name}
              </p>
            </div>
          </div>
          <Button
            className="audience-insights__header-button"
            theme={BUTTON_THEME.SECONDARY}
            link={`/audience-creator/${audienceId}`}
          >
            Open in Audience Creator
            <Icon width="24px" height="24px" icon="externallink" type="outline" />
          </Button>
          <Button
            className="audience-insights__header-button"
            theme={BUTTON_THEME.SECONDARY}
            onClick={exportModal.handleOpenModal}
          >
            Export
          </Button>
          <div className="audience-insights__save-button">
            <Button onClick={handleSave} className="audience-insights__save-text">
              Save
            </Button>
          </div>
        </div>
      </Header>
      {error?.error ? (
        <div className="audience-insights__error-wrapper">
          <h2 className="audience-insights__error-header">Something went wrong!</h2>
          <p className="audience-insights__error-message">{error.message}</p>
          <div className="audience-insights__error-code">
            <p>ID: {audienceId}</p>
            <p>Time: {new Date().toISOString()}</p>
          </div>
          <Button onClick={() => props.history.go(0)}>Try again</Button>
        </div>
      ) : loading ? (
        <BarLoader />
      ) : (
        report?.slides && (
          <AudienceInsightsLayout
            audience={audience}
            audienceId={audienceId}
            slides={report.slides}
            index={state.activeSlideIndex}
            categoryOverindex={categoryOverindex}
            onChangeSlide={(activeSlideIndex) => updateState({ activeSlideIndex })}
            onFieldChange={onFieldChange}
            onChangeSlideOrder={onChangeSlideOrder}
            onAddSlide={addSlide}
            onDuplicateSlide={duplicateSlide}
            onDeleteSlide={deleteSlide}
          />
        )
      )}
      <If condition={state.show_faq}>
        <FAQ
          audience_id={audienceId}
          has_audience={audience && audience.name}
          onClose={() => updateState({ show_faq: false })}
        />
      </If>
    </div>
  )
}

// export
export default AudienceInsights
